import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Phone, MessageCircle, ArrowDown } from "lucide-react";
import { SITE } from "../../lib/content";
import { MaskLine } from "./Reveal";
import { scrollTo } from "./Navbar";

const HERO_IMG =
  "https://customer-assets-rejwkqb3.emergentagent.net/job_nk-science-academy/artifacts/mpdubsph_file_00000000836481faa39e4a9474153e89.png";

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);

  return (
    <section
      id="home"
      ref={ref}
      data-testid="hero-section"
      className="relative bg-ink text-white overflow-hidden noise"
    >
      <div className="absolute inset-0 grid-lines opacity-[0.5] pointer-events-none" />
      <div className="relative z-10 max-w-7xl mx-auto px-6 pt-16 pb-20 lg:pt-24 lg:pb-28 grid lg:grid-cols-12 gap-10 items-center">
        {/* Left copy */}
        <div className="lg:col-span-7">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.1 }}
            className="flex items-center gap-3 mb-8"
          >
            <span className="h-px w-10 bg-flame" />
            <span className="text-xs font-semibold uppercase tracking-[0.25em] text-white/60">
              Best Tuition Centre in Hajo · Kamrup, Assam
            </span>
          </motion.div>

          <h1 className="font-heading font-black tracking-tighter leading-[0.92] text-[15vw] sm:text-[12vw] lg:text-[6.4rem] xl:text-[7rem]">
            <MaskLine delay={0.05} mount>LEARN.</MaskLine>
            <MaskLine delay={0.18} mount className="text-stroke">UNDERSTAND.</MaskLine>
            <MaskLine delay={0.31} mount>
              EX<span className="text-flame">CEL.</span>
            </MaskLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6, duration: 0.7 }}
            className="mt-8 max-w-md text-white/70 text-base sm:text-lg leading-relaxed"
          >
            Quality coaching for <span className="text-white font-semibold">Class 9–12</span> — Maths,
            Science &amp; Physics (CBSE &amp; AHSEC) — with personal attention and concept-based learning.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.75, duration: 0.7 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <a
              href={`tel:${SITE.phoneIntl}`}
              data-testid="hero-call-button"
              className="group inline-flex items-center gap-2 bg-flame text-white font-semibold px-7 py-3.5 rounded-full hover:bg-white hover:text-ink transition-colors duration-300"
            >
              <Phone className="h-4 w-4" /> Call {SITE.phone}
            </a>
            <a
              href={`https://wa.me/${SITE.whatsapp}?text=Hi%2C%20I'd%20like%20to%20know%20more%20about%20admissions%20at%20NK%20Science%20%26%20Physics%20Academy.`}
              target="_blank"
              rel="noreferrer"
              data-testid="hero-whatsapp-button"
              className="inline-flex items-center gap-2 border border-white/25 text-white font-semibold px-7 py-3.5 rounded-full hover:border-flame hover:text-flame transition-colors duration-300"
            >
              <MessageCircle className="h-4 w-4" /> WhatsApp
            </a>
          </motion.div>
        </div>

        {/* Right image — clipped, parallax */}
        <div className="lg:col-span-5">
          <motion.div
            initial={{ opacity: 0, scale: 0.94 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.35, duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
            className="relative aspect-[4/5] overflow-hidden rounded-t-[999px] border border-white/15 shadow-[inset_0_0_120px_rgba(0,0,0,0.55)]"
          >
            <motion.img
              src={HERO_IMG}
              alt="Student studying physics and mathematics"
              style={{ y, scale }}
              className="absolute inset-0 h-[115%] w-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/10 to-transparent" />
            <div className="absolute bottom-6 left-6 right-6 flex items-center justify-between text-xs uppercase tracking-widest text-white/80">
              <span>Concept-first</span>
              <span className="text-flame">Est. Kamrup</span>
            </div>
          </motion.div>
        </div>
      </div>

      <button
        onClick={() => scrollTo("about")}
        className="relative z-10 mx-auto mb-8 flex items-center gap-2 text-white/50 hover:text-flame text-xs uppercase tracking-widest transition-colors"
        data-testid="scroll-down"
      >
        <ArrowDown className="h-4 w-4 animate-bounce" /> Scroll to explore
      </button>
    </section>
  );
}
